import { EditorAnalyticsProvider } from '@/components/tiptap/shared/editor-analytics-provider';
import { SelectionAiActions } from '@/components/tiptap/shared/selection-ai-actions';
import { TextMenuWrapper } from '@/components/tiptap/shared/text-menu-base';
import {
  BoldButton,
  CodeBlockButton,
  ConvertToSectionButton,
  CopyButton,
  FindReplaceButton,
  ItalicButton,
  SnippetButton,
  UnderlineButton,
} from '@/components/tiptap/shared/text-menu-buttons';
import { TextStyleDropdown } from '@/components/tiptap/shared/text-style-dropdown';
import { useSelectionAiMenuShownTracking } from '@/components/tiptap/shared/use-selection-ai-menu-shown-tracking';
import { useSelectionCapture } from '@/components/tiptap/shared/use-selection-capture';
import { type EditorType } from '@/components/tiptap/types/index.enums';
import { useEmphasisDisabledTooltip } from '@/features/copySettings';
import {
  useIsScribeBlockEditorCodeblockEnabled,
  useIsSelectionAskHeidiEnabled,
} from '@/features/flags';
import { type Editor } from '@tiptap/react';
import { Fragment } from 'react';
import { useTextMenuCommands } from './hooks/useTextmenuCommands';
import { useTextMenuStates } from './hooks/useTextmenuStates';

const SELECTION_MENU_ICON_SIZE = 16;

export const TextMenu = ({
  editor,
  editorType,
  emphasisDisabled = false,
  enableFindReplace,
  enableSnippet,
  enableConvertToSection,
  selector,
}: {
  editor: Editor;
  editorType?: EditorType;
  /** Explicit NoteTab scope; EditorType.NOTE is also used by Haiku. */
  emphasisDisabled?: boolean;
  enableFindReplace: boolean;
  enableSnippet: boolean;
  enableConvertToSection: boolean;
  selector: string;
}) => {
  const {
    onBold,
    onItalic,
    onCopy,
    onFindReplace,
    onCreateSnippet,
    onUnderline,
    onCodeBlock,
    onConvertToSection,
  } = useTextMenuCommands(editor, editorType);
  const {
    isHeading,
    isBold,
    isItalic,
    isUnderline,
    isCodeBlock,
    canConvertToSection,
    shouldShow,
  } = useTextMenuStates({
    editor,
    selector,
  });
  const codeBlockEnabled = useIsScribeBlockEditorCodeblockEnabled();
  const isSelectionAskHeidiEnabled = useIsSelectionAskHeidiEnabled();
  const { trackMenuShown, trackMenuHidden } = useSelectionAiMenuShownTracking({ editor });
  useSelectionCapture(editor, isSelectionAskHeidiEnabled);
  // Supplied by the host because EditorType.NOTE is broader than NoteTab.
  const emphasisDisabledTooltip = useEmphasisDisabledTooltip();

  if (isSelectionAskHeidiEnabled) {
    // v5.2 selection menu: text-style dropdown, compact formatting row,
    // editor actions in the "More options" submenu.
    return (
      <EditorAnalyticsProvider editorType="block_markdown">
        <TextMenuWrapper
          editor={editor}
          shouldShow={shouldShow}
          onShow={trackMenuShown}
          onHide={trackMenuHidden}
          footer={
            <SelectionAiActions
              editor={editor}
              trackShownOnSelectionUpdate={false}
              editorActions={{
                enableFindReplace,
                enableSnippet,
                enableConvertToSection: enableConvertToSection && canConvertToSection,
                onFindReplace,
                onCreateSnippet,
                onConvertToSection,
              }}
            />
          }
        >
          <div className="flex flex-row items-center gap-0.5">
            <TextStyleDropdown editor={editor} iconSize={SELECTION_MENU_ICON_SIZE} />
            {!isHeading && (
              <BoldButton
                onClick={onBold}
                active={isBold}
                disabled={emphasisDisabled}
                disabledTooltip={emphasisDisabledTooltip}
                size="md"
                iconSize={SELECTION_MENU_ICON_SIZE}
              />
            )}
            <ItalicButton
              onClick={onItalic}
              active={isItalic}
              disabled={emphasisDisabled}
              disabledTooltip={emphasisDisabledTooltip}
              size="md"
              iconSize={SELECTION_MENU_ICON_SIZE}
            />
            <UnderlineButton
              onClick={onUnderline}
              active={isUnderline}
              disabled={emphasisDisabled}
              disabledTooltip={emphasisDisabledTooltip}
              size="md"
              iconSize={SELECTION_MENU_ICON_SIZE}
            />
            <CopyButton onClick={onCopy} size="md" iconSize={SELECTION_MENU_ICON_SIZE} />
          </div>
        </TextMenuWrapper>
      </EditorAnalyticsProvider>
    );
  }

  return (
    <EditorAnalyticsProvider editorType="block_markdown">
      <TextMenuWrapper editor={editor} shouldShow={shouldShow}>
        <Fragment>
          <BoldButton
            onClick={onBold}
            active={isBold}
            disabled={emphasisDisabled}
            disabledTooltip={emphasisDisabledTooltip}
          />
          <ItalicButton
            onClick={onItalic}
            active={isItalic}
            disabled={emphasisDisabled}
            disabledTooltip={emphasisDisabledTooltip}
          />
          <UnderlineButton
            onClick={onUnderline}
            active={isUnderline}
            disabled={emphasisDisabled}
            disabledTooltip={emphasisDisabledTooltip}
          />
          {codeBlockEnabled && (
            <CodeBlockButton onClick={onCodeBlock} active={isCodeBlock} />
          )}
          {enableConvertToSection && canConvertToSection && (
            <ConvertToSectionButton onClick={onConvertToSection} />
          )}
          <CopyButton onClick={onCopy} />
          {enableFindReplace && (
            <FindReplaceButton
              onClick={onFindReplace}
              editor={editor}
              data-testid="text-menu-find-replace"
            />
          )}
          {enableSnippet && <SnippetButton onClick={onCreateSnippet} editor={editor} />}
        </Fragment>
      </TextMenuWrapper>
    </EditorAnalyticsProvider>
  );
};
